import React, { useMemo } from 'react';
import { graphql } from 'gatsby';
import { Container, Text, Row, Col, Heading } from 'design-system';
import style from './styles.module.scss';
import ButtonComponent from '../ButtonComponent';
import FrameImageComponent from '../FrameImageComponent';
import ComaComponent from '../ComaComponent';

export default function HeroSection({
    pretitle,
    title,
    text,
    image,
    button,
    titleWider,
    imageType,
    light,
    type,
    aspectRatio,
    addComma,
    removeFrameFromImage,
    nextSectionType,
}) {
    const isSmall = useMemo(() => {
        return type === 'small';
    }, [type]);

    const finalImageType = useMemo(() => {
        if (imageType) {
            return imageType;
        }

        return removeFrameFromImage ? 'none' : 'hero-dark';
    }, [imageType, removeFrameFromImage]);

    const finalTitleWider = useMemo(() => {
        if (titleWider || addComma) {
            return true;
        }

        return finalImageType.indexOf('none') !== -1;
    }, [finalImageType, addComma, titleWider]);

    const pb = useMemo(() => {
        if (nextSectionType === 'DatoCmsContactFormSection') {
            return { lg: 20 };
        }

        if (titleWider) {
            return { lg: 10 };
        }

        return {};
    }, [nextSectionType, titleWider]);

    return (
        <section
            className={[
                style.wrapper,
                style[`wrapper--${light ? 'light' : 'dark'}`],
                image
                    ? `${style['wrapper--with-image']} ${
                          style[`wrapper--with-image--${finalImageType}`]
                      }`
                    : '',
            ].join(' ')}>
            <Container
                pt={{
                    xs: 10,
                }}
                pb={pb}>
                <Row className="d-lg-flex align-items-lg-center">
                    <Col
                        offset={{ lg: 1 }}
                        col={{
                            lg: !image ? 7 : 5,
                            xl: finalTitleWider ? (!image ? 7 : 5) : 4,
                        }}
                        pb={{
                            xs: isSmall ? 2 : 20,
                            lg: isSmall ? 30 : 20,
                        }}>
                        {pretitle ? (
                            <Text
                                className={`subtitle text-uppercase font-weight-bold mb-3 mb-lg-2 ${style.pretitle}`}
                                tag="p">
                                {pretitle}
                            </Text>
                        ) : null}
                        <Heading
                            className={`${
                                isSmall
                                    ? 'h1'
                                    : `d1 ${
                                          title &&
                                          title.indexOf(' ') === -1 &&
                                          title.length > 12
                                              ? style['title--large']
                                              : ''
                                      }`
                            } ${addComma ? 'mb-lg-10' : 'mb-3 mb-lg-5'} ${
                                style.title
                            }`}
                            tag="h1">
                            {title}
                        </Heading>
                        {text ? (
                            <Text
                                className={`h6 ${style.text} mb-lg-5`}
                                tag={
                                    text && text.indexOf('<p>') === -1
                                        ? 'p'
                                        : 'div'
                                }>
                                {text}
                            </Text>
                        ) : null}
                        {addComma ? (
                            <ComaComponent dark={true} className={style.coma} />
                        ) : null}
                        {button ? (
                            <ButtonComponent
                                button={{
                                    type: 'primary-xl',
                                    ...button,
                                }}
                                dark={true}
                                buttonClassName={'mt-6 mb-6'}
                            />
                        ) : null}
                    </Col>
                    {image && (
                        <Col
                            offset={{ lg: 1 }}
                            col={{
                                lg: 5,
                                xl: finalTitleWider ? 5 : 6,
                            }}>
                            <FrameImageComponent
                                className={style.image}
                                image={image}
                                aspectRatio={aspectRatio ? aspectRatio : 1.03}
                                desktopAspectRatio={
                                    aspectRatio ? aspectRatio : 1.23
                                }
                                type={finalImageType}
                            />
                        </Col>
                    )}
                </Row>
            </Container>
        </section>
    );
}

export const query = graphql`
    fragment DatoCmsHeroSection on DatoCmsHeroSection {
        pretitle
        title
        text
        image {
            alt
            gatsbyImageData(
                imgixParams: { w: "960", h: "1224", fit: "crop" }
                placeholder: BLURRED
                layout: FIXED
            )
        }
        removeFrameFromImage
        addComma
        titleWider
        button {
            ...Button
        }
    }
`;
